import { useState, useEffect, useRef, useCallback } from 'react'
import Formsy from 'formsy-react'
import { useNavigate, useLocation } from 'react-router-dom'
import Button from '@/components/ui/button'
import InputText from '@/components/ui/InputText'
import Helmet from '@/components/helmet'
import LoginError from '@/features/auth/login-error'
import WalletAuthButton from '@/features/auth/wallet-auth/WalletAuthButton'
import constants from '@/constants'
import { i18n } from '@/locale/i18n'
import Modal from '@/layouts/modal/Modal'

import {
  loginEmail,
  loginMagic,
  logout,
  selectCurrentUser,
  selectSessionError,
  selectSessionStatus,
} from '@/store/account'
import { loginResetUrl, termsUrl, privacyPolicyUrl, feedUrl } from '@/urls'
import { useDispatch, useSelector } from 'react-redux'
import { useModalActions } from '@/hooks'
import { userIsAnonymous } from '@/api/auth'
import { toastService } from '@/api/toast'
import { TextFieldFormsy } from '@/components/ui/text-field'
import mixpanelService from '@/services/analytics/Mixpanel'
import { CLICK_COMPONENT, CLICK_ELEMENT, CLICK_SOURCE } from '@/constants/analyticsClick'
import './ViewLogin.scss'
import { urlSearchParams } from '@/util/urlSearchParams'
import useLastPath from '@/hooks/useLastPath'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { ensureBufferPolyfill } from '@/polyfills'

function preloadMagic() {
  return ensureBufferPolyfill().then(() => import('@/services/magicConfig'))
}

async function getMagic() {
  await ensureBufferPolyfill()
  const { getMagicInstance } = await import('@/services/magicConfig')
  return getMagicInstance()
}

/** Persisted after successful email (Magic OTP) login for prefilling the login modal. */
const LOGIN_LAST_EMAIL_STORAGE_KEY = 'loginLastEmail'

/** Snappier than the default sheet bounce — open/close should feel immediate. */
const LOGIN_SHEET_TWEEN = { ease: [0.32, 0.72, 0, 1], duration: 0.2 }
const LOGIN_SHEET_CLOSE_MS = 240

export const ViewLoginPages = {
  login_form: 'login_form',
  two_factor_auth: 'two_factor_auth',
}

const ViewLogin = () => {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const [redirecting, setRedirecting] = useState(false)
  const [valid, setValid] = useState(false)
  const [currentPage, setCurrentPage] = useState(ViewLoginPages.login_form)
  const [userData, setUserData] = useState({})
  const [isOpen, setIsOpen] = useState(true)
  const error = useSelector(selectSessionError)
  const currentUser = useSelector(selectCurrentUser)
  const sessionStatus = useSelector(selectSessionStatus)
  const modalActions = useModalActions()
  const params = new urlSearchParams(location.search)
  const [clearToken, setClearToken] = useState(params.get('clearToken'))
  const dispatch = useDispatch()
  const { getLastPath, setLastPath, clearLastPath } = useLastPath()
  const isMobileSize = useIsMobileSize()
  const closingRef = useRef(false)
  const USE_MAGIC_LABS = true
  const loginFormRef = useRef(null)

  useEffect(() => {
    const returnUrl = routerLocation.state?.returnUrl
    if (returnUrl) {
      setLastPath(returnUrl)
    }
  }, [routerLocation.state?.returnUrl, setLastPath])

  const [magicLoading, setMagicLoading] = useState(false)
  const [magicWalletAddress, setMagicWalletAddress] = useState(null)
  const [magicLoadingStep, setMagicLoadingStep] = useState(null)
  const [magicError, setMagicError] = useState(null)
  const [socialLoading, setSocialLoading] = useState(null)
  // Email field validation: format error only shows after blur; required error shows on submit.
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailFieldError, setEmailFieldError] = useState('')
  const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const handleMagicAuth = async formData => {
    const email = (formData?.email || userData?.email || '').trim()

    // Button is always active: validate on submit and surface errors instead of bailing silently.
    if (!email) {
      setEmailTouched(true)
      setEmailFieldError(i18n('login.email_required'))
      return
    }
    if (!isValidEmail(email)) {
      setEmailTouched(true)
      setEmailFieldError(i18n('login.email_invalid'))
      return
    }
    setEmailFieldError('')

    setMagicLoading(true)
    setMagicError(null)
    setMagicLoadingStep('magic_auth')
    setMagicWalletAddress(null)

    try {
      const magic = await getMagic()
      const didTokenFromOtp = await magic.auth.loginWithEmailOTP({
        email: email,
        showUI: true,
      })

      const didToken =
        didTokenFromOtp ?? (await magic.user.getIdToken?.())
      if (!didToken) {
        toastService('fail', { title: i18n('hardcoded.could_not_get_login_token') })
        setMagicLoadingStep(null)
        setMagicLoading(false)
        return
      }

      setMagicLoadingStep('backend_auth')
      const userInfo = await magic.user.getInfo?.()
      const walletAddress =
        userInfo?.wallets?.ethereum?.publicAddress ??
        userInfo?.publicAddress ??
        ''
      setMagicWalletAddress(walletAddress || null)

      const given_name = userInfo?.given_name ?? userInfo?.givenName ?? ''
      const family_name = userInfo?.family_name ?? userInfo?.familyName ?? ''
      const name = userInfo?.name ?? (given_name || family_name ? [given_name, family_name].filter(Boolean).join(' ') : '')
      const metadata = { given_name, family_name, name }

      await dispatch(loginMagic(didToken, email, walletAddress, metadata, 'magic_email_OTP'))

      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(LOGIN_LAST_EMAIL_STORAGE_KEY, String(email).trim())
        }
      } catch {
        /* ignore quota / private mode */
      }

      setMagicLoadingStep('complete')
    } catch (error) {
      console.error('Magic authentication error:', error)
      let errorMessage = 'Authentication failed. Please try again.'
      if (error?.message) {
        if (error.message.includes('User denied') || error.message.includes('User rejected')) {
          errorMessage = 'Authentication was cancelled.'
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address.'
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection.'
        } else {
          const { getMagicAuthErrorMessage } = await import('@/util/authErrorMessages')
          errorMessage = getMagicAuthErrorMessage(error)
        }
      }
      setMagicError(errorMessage)
      toastService('fail', { title: errorMessage })
      setMagicLoadingStep(null)
    } finally {
      setMagicLoading(false)
    }
  }

  const handleMagicGoogleLogin = async () => {
    mixpanelService.trackClick({
      component: CLICK_COMPONENT.LOGIN,
      element: CLICK_ELEMENT.SOCIAL_GOOGLE,
      source: CLICK_SOURCE.LOGIN_PAGE,
    })
    setSocialLoading('google')
    try {
      const magic = await getMagic()
      const isLoggedIn =
        typeof magic.user.isLoggedIn === 'function'
          ? await magic.user.isLoggedIn()
          : magic.user.isLoggedIn
      if (isLoggedIn) await magic.user.logout()
      await magic.oauth2.loginWithRedirect({
        provider: 'google',
        redirectURI: window.location.origin + '/callback',
      })
    } catch (error) {
      console.error('Magic Google login error:', error)
      toastService('fail', { title: i18n('hardcoded.google_login_failed') })
      setSocialLoading(null)
    }
  }

  const handleMagicFacebookLogin = async () => {
    setSocialLoading('facebook')
    try {
      const magic = await getMagic()
      const isLoggedIn =
        typeof magic.user.isLoggedIn === 'function'
          ? await magic.user.isLoggedIn()
          : magic.user.isLoggedIn
      if (isLoggedIn) await magic.user.logout()
      await magic.oauth2.loginWithRedirect({
        provider: 'facebook',
        redirectURI: window.location.origin + '/callback',
      })
    } catch (error) {
      console.error('Magic Facebook login error:', error)
      toastService('fail', { title: i18n('hardcoded.facebook_login_failed') })
      setSocialLoading(null)
    }
  }

  const handleAuthRedirect = () => {
    const searchParams = new urlSearchParams(window.location.search)
    if (searchParams.has('redirect')) {
      const redirectTo = searchParams.get('redirect')
      if (redirectTo === 'community') {
        if (typeof window !== 'undefined') {
          window.location.href = import.meta.env.VITE_COMMUNITY_URL
          return
        }
      } else {
        window.location.href = redirectTo
        return
      }
    }
    const returnUrl = routerLocation.state?.returnUrl
    const lastPath = getLastPath()
    const destination = returnUrl || (lastPath !== feedUrl() ? lastPath : feedUrl())
    clearLastPath()
    if (destination !== location.pathname) navigate(destination)
  }

  useEffect(() => {
    if (clearToken) return
    if (routerLocation.state?.returnUrl) return
    userIsAnonymous().then(isAnonymous => {
      if (!isAnonymous) {
        handleAuthRedirect()
      }
    })
  }, [clearToken])

  useEffect(() => {
    if (clearToken) {
      dispatch(logout())
      setClearToken(false)
    }
  }, [clearToken])

  useEffect(() => {
    if (currentUser && !clearToken) {
      setRedirecting(true)
      handleAuthRedirect()
    }
  }, [currentUser, clearToken])

  useEffect(() => { 
    if (error && error?.error?.error === 'missing_2fa_token') {
      setCurrentPage(ViewLoginPages.two_factor_auth)
    }
  }, [error])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const saved = window.localStorage.getItem(LOGIN_LAST_EMAIL_STORAGE_KEY)
      const email = saved?.trim()
      if (!email) return
      loginFormRef.current?.updateInputsWithValue?.({ email })
      setUserData(prev => ({ ...prev, email }))
    } catch {
      /* ignore */
    }
  }, [])

  const onFormValid = () => {
    if (!valid) {
      setValid(true)
    }
  }

  const onFormInvalid = () => {
    if (valid) {
      setValid(false)
    }
  }

  const onFormSubmit = async data => {
    setUserData(currentData => ({ ...currentData, ...data }))
    setMagicError(null)

    if (currentPage === ViewLoginPages.two_factor_auth) {
      dispatch(
        loginEmail({
          email: data.email || userData.email,
          password: data.password || userData.password,
          authCode: data.auth_code,
        })
      )
    } else if (currentPage === ViewLoginPages.login_form) {
      if (USE_MAGIC_LABS) {
        await handleMagicAuth(data)
      } else {
        dispatch(loginEmail({ email: data.email, password: data.password }))
      }
    }
  }

  const renderHelmet = () => {
    return <Helmet title={i18n('signin.title')} follow={false} />
  }
  const renderLoginForm = () => {
    return (
      <>
        <div className="flex gap-3 mb-2">
          <div className="flex-1">
            <Button
              onClick={handleMagicGoogleLogin}
              onMouseEnter={preloadMagic}
              onFocus={preloadMagic}
              onPointerDown={preloadMagic}
              disabled={magicLoading || !!socialLoading}
              className="gap-2 !justify-center !text-[14px] !bg-[#131314] hover:!bg-[#333338] !box-border !h-[56px] !min-h-[56px] !max-h-[56px] !py-0 !px-4"
              type="solid"
              size="full"
              loading={socialLoading === 'google'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span className="text-[14px] text-white">{i18n('login.continue_with_google')}</span>
            </Button>
          </div>
          {/* <div className="flex-1">
            <Button
              onClick={handleMagicFacebookLogin}
              disabled={magicLoading || !!socialLoading}
              className="gap-2 !bg-[#1877F2] hover:!bg-[#004298] !px-1"
              type="solid"
              size="full"
              loading={socialLoading === 'facebook'}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 50 50" fill="white">
                <path d="M25,3C12.85,3,3,12.85,3,25c0,11.03,8.125,20.137,18.712,21.728V30.831h-5.443v-5.783h5.443v-3.848 c0-6.371,3.104-9.168,8.399-9.168c2.536,0,3.877,0.188,4.512,0.274v5.048h-3.612c-2.248,0-3.033,2.131-3.033,4.533v3.161h6.588 l-0.894,5.783h-5.694v15.944C38.716,45.318,47,36.137,47,25C47,12.85,37.15,3,25,3z"></path>
              </svg>
              <span className="text-white">Facebook</span>
            </Button>
          </div> */}
        </div>

        <WalletAuthButton
          disconnectIfConnected
          label={i18n('login.login_via_wallets')}
          className="w-full flex items-center justify-center gap-2 !h-[56px] !min-h-[56px] !max-h-[56px] !px-4 !py-0 !text-[14px] border !border-Neutral-3 rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <Formsy
          ref={loginFormRef}
          className="login__form"
          onValid={onFormValid}
          onInvalid={onFormInvalid}
          onValidSubmit={onFormSubmit}
          onChange={currentValues => setUserData(currentData => ({ ...currentData, ...currentValues }))}>
          <div className="flex gap-3 items-center my-3">
            <hr className="border-Neutral-3 w-full" />
            <span className="text-Neutral-5 text-sm">{i18n('login.or_separator')}</span>
            <hr className="border-Neutral-3 w-full" />
          </div>

          {USE_MAGIC_LABS ? (
            <div className="login__row mb-4">
              <InputText
                variant="button"
                buttonLabel={i18n('login.continue_submit')}
                placeholder={i18n('login.email_address_placeholder')}
                value={userData.email || ''}
                onChange={email => {
                  setUserData(prev => ({ ...prev, email }))
                  setEmailFieldError('')
                }}
                onBlur={() => setEmailTouched(true)}
                onFocus={preloadMagic}
                onEnter={email => onFormSubmit({ email })}
                disabled={sessionStatus === constants.LOADING || redirecting || magicLoading}
                error={
                  emailFieldError ||
                  magicError ||
                  (emailTouched && userData.email && !isValidEmail(userData.email)
                    ? i18n('login.email_invalid')
                    : '')
                }
                showClear={false}
              />
              <LoginError error={error} />
            </div>
          ) : (
            <>
              <div className="login__row mb-4">
                <TextFieldFormsy
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={i18n('login.email_address_placeholder')}
                  label=""
                  validations="isEmail"
                  validationError={i18n('login.email_invalid')}
                  required
                  className="w-full not-visited:border border-Neutral-3 rounded-lg text-Neutral-5 placeholder-Neutral-4"
                />
              </div>

              <LoginError error={error} />

              <div className="login__row mb-4">
                <TextFieldFormsy
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="your password"
                  label=""
                  required
                  className="w-full border border-Neutral-3 rounded-lg text-Neutral-5 placeholder-Neutral-4"
                />
              </div>

              <Button
                flat
                size="full"
                className="w-full py-3 bg-Neutral-3 text-Neutral-5 rounded-lg cursor-not-allowed mb-4"
                loading={sessionStatus === constants.LOADING || redirecting}
                onClick={() =>
                  mixpanelService.trackClick({
                    component: CLICK_COMPONENT.LOGIN,
                    element: CLICK_ELEMENT.SUBMIT,
                    source: CLICK_SOURCE.LOGIN_PAGE,
                  })
                }
                disabled={sessionStatus === constants.LOADING || !valid}>
                {i18n('login.login_button')}
              </Button>
            </>
          )}

          <div className="text-center">
            <span className="text-xs">
              <a href={termsUrl()} target="_blank" rel="noopener noreferrer" className="link hover:underline">
                Terms of Service
              </a>{' '}
              ·{' '}
              <a href={privacyPolicyUrl()} target="_blank" rel="noopener noreferrer" className="link hover:underline">
                Privacy Policy
              </a>
            </span>
          </div>

          {!USE_MAGIC_LABS && (
            <div className="text-center py-6">
              <a href={loginResetUrl()} className="w-full text-center font-bold link">
                {i18n('login.forgot_password')}
              </a>
            </div>
          )}

          {/* <div className="text-center mt-6">
            {i18n('login.dont_have_account')}{' '}
            <Link className="login__small-link" to={signupUrl()}>
              {i18n('login.register')}
            </Link>
          </div> */}
        </Formsy>
      </>
    )
  }

  const renderTwiFactorAuth = () => {
    return (
      <>
        <p className="text-center">{i18n('login.two_factor_auth.paragraph')} </p>

        <Formsy
          className="login__form mt-6"
          onValid={onFormValid}
          onInvalid={onFormInvalid}
          onValidSubmit={onFormSubmit}>
          <div className="login__row">
            <TextFieldFormsy
              name="auth_code"
              type="text"
              placeholder={'Add code'}
              label={'Code'}
              validationError={'wrong code'}
              required
              validations="maxLength:6"
            />
          </div>
          {error && error.error.error === 'invalid_2fa_token' && (
            <div className="login-error__message">{i18n('login.invalid_2fa_token')}</div>
          )}

          <Button
            flat
            size="full"
            color="blue"
            loading={sessionStatus === constants.LOADING || redirecting}
            disabled={sessionStatus === constants.LOADING || !valid}>
            {i18n('login.login_button')}
          </Button>
        </Formsy>
      </>
    )
  }
  const onClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    if (isMobileSize) {
      setIsOpen(false)
      window.setTimeout(() => modalActions.close(), LOGIN_SHEET_CLOSE_MS)
    } else {
      modalActions.close()
    }
  }, [isMobileSize, modalActions])

  return (
    <Modal
      className="view-login"
      isOpen={isOpen}
      onCloseRequested={onClose}
      position="bottom"
      title={i18n('login.modal_welcome_title')}
      tweenConfig={LOGIN_SHEET_TWEEN}
      content={
        <>
          {currentPage === ViewLoginPages.login_form && renderLoginForm()}
          {currentPage === ViewLoginPages.two_factor_auth && renderTwiFactorAuth()}
          {renderHelmet()}
        </>
      }
      showCloseButton={true}
    />
  )
}

export default ViewLogin
